import React from 'react'
import * as R from 'remeda'
import { Box, Flex, styled, Text } from '@chilipiper/design-system'
import {
  Button,
  IconButton,
  UnstyledButton,
  getTextStyleCss,
} from '@chilipiper/design-system/src/new'
import { IconX } from '@chilipiper/icons/figma-generated/src/icon'
import { sanitize } from '@chilipiper/utils'
import {
  ConversationMessage,
  ConversationAction,
  ReplyButton,
  MessagePart,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { useMessageFeedContext } from '../../chat-widget/MessageFeedContext'
import { parseMarkdown, markdownStyles } from '../../helpers/markdown'
import { ConsentFooter } from '../ConsentFooter'
import { ChatInput } from '../ChatInput'
import { StyledAvatarImage } from '../styles'
import { useAppearanceSettings } from '../ChiliTheme'

interface Props {
  action?: ConversationAction
  messages?: ConversationMessage[]
  onCallToActionClick: () => void
  onClose?: () => void
  onReplyButtonClick?: (reply: ReplyButton, index: number) => void
  ref?: React.Ref<HTMLDivElement | null>
  onSendMessage?: (message: string) => void
}

export const BotPreview = ({
  ref,
  messages,
  action,
  onClose,
  onReplyButtonClick,
  onCallToActionClick,
  onSendMessage,
}: Props) => {
  const { channel, disableInteractions } = useMessageFeedContext()
  const appearanceSettings = useAppearanceSettings()

  if (!channel) {
    return null
  }

  const messagesContents = messages?.reduce<Array<MessagePart>>((acc, message) => {
    if (message.type === 'Content') {
      return [...acc, ...message.value]
    }
    if (message.type === 'PiperAiContent') {
      return [...acc, message.value]
    }
    return acc
  }, [])

  const textBlocks = messagesContents?.filter(block => block.type === 'Text') ?? []

  const isAiStart = messages?.every(message => message.type === 'PiperAiContent')

  return (
    <StyledContainer
      inert={disableInteractions}
      ref={ref}
      w='368px'
      borderRadius={20}
      data-test-id='BotPreview'
      boxShadow='border/section'
    >
      <Flex flexDirection='column'>
        <BotPreviewHeader avatar={channel.avatar} name={channel.fullName} onClose={onClose} />
        {textBlocks.map((textBlock, index) => (
          <StyledText
            key={index}
            color='fg/major'
            textStyle='body-multiline'
            display='block'
            px='section'
            pb={index === textBlocks.length - 1 ? 'section' : 1}
            data-test-id='BotPreview-text'
            dangerouslySetInnerHTML={{
              __html: isAiStart
                ? parseMarkdown(textBlock.text ?? '')
                : sanitize(textBlock.text?.trim() ?? '', ['a'], ['target']),
            }}
          />
        ))}
        {isAiStart ? (
          <>
            {action?.type === 'EnterPiperAiChat' ? (
              <Flex gap={2} px='xs' pb='xs' flexWrap='wrap'>
                {action.shortcuts?.map((reply, index) => {
                  return (
                    <Shortcut key={index} onClick={() => onReplyButtonClick?.(reply, index)}>
                      {reply.text}
                    </Shortcut>
                  )
                })}
              </Flex>
            ) : null}
            <Box mx={3}>
              <ChatInput
                sendMessage={onSendMessage ?? R.doNothing()}
                sendingMessage={false}
                isDisabled={false}
              />
            </Box>
          </>
        ) : (
          <Flex flexDirection='column' gap={2} px={4}>
            {action?.type === 'SelectReplyAction' ? (
              action.replies.map((reply, index) => {
                return (
                  <ReplyButtonComponent
                    key={index}
                    text={reply.text}
                    onClick={() => onReplyButtonClick?.(reply, index)}
                  />
                )
              })
            ) : (
              <ReplyButtonComponent text='Hello 👋' onClick={onCallToActionClick} />
            )}
          </Flex>
        )}
      </Flex>
      <Box px={3} py={2}>
        <ConsentFooter consentFooter={appearanceSettings?.consentFooter || ''} />
      </Box>
    </StyledContainer>
  )
}

const Shortcut = styled(UnstyledButton)`
  ${getTextStyleCss('multiline-button')}
  word-break: break-word;
  border-radius: 12px;
  padding: ${({ theme }) => theme.space['3xs']} ${({ theme }) => theme.space['xs']};
  box-shadow: 0px 0px 0px 1px rgb(var(--primary-color)) inset;
  color: rgb(var(--primary-color));

  :hover,
  :focus-visible {
    background: rgb(var(--primary-color));
    color: rgb(var(--primary-contrast-color));
  }
`

const ReplyButtonComponent = ({ text, onClick }: { onClick: () => void; text: string }) => {
  return (
    <Button variant='moderate' onClick={onClick} data-test-id='BotPreview-replyButton' isFullWidth>
      {text}
    </Button>
  )
}

const StyledText = styled(Text)`
  ${markdownStyles}
`

const BotPreviewHeader = ({
  avatar,
  name,
  onClose,
}: {
  avatar: string | React.ReactNode | undefined
  name: string
  onClose?: () => void
}) => (
  <Flex justifyContent='space-between' p='section'>
    <Flex flexDirection='column' gap='margin-form-item-content'>
      {typeof avatar === 'string' ? (
        <StyledAvatarImage src={avatar} alt='' size='56px' borderRadius='12px' />
      ) : (
        <Box h='56px' w='56px'>
          {avatar}
        </Box>
      )}
      <Text color='fg/major' textStyle='heading-m' data-test-id='WidgetHeader-name'>
        {name}
      </Text>
    </Flex>
    <IconButton
      size={7}
      label='Close chat'
      icon={IconX}
      onClick={onClose}
      data-test-id='WidgetHeader-close'
      variant='ghost'
    />
  </Flex>
)

const StyledContainer = styled(Box)`
  position: relative;
  background:
    radial-gradient(
      100% 55% at 6.4% -10.49%,
      rgba(var(--primary-color), 0.48) 0%,
      transparent 100%
    ),
    ${({ theme }) => theme.colors['bg/minor']};
`
